import { logRumError } from 'lib/DDRum';
import { persistStateTypes } from 'a/reducers/persistStateReducer';

import {
  GAME_ID,
  REDUX_ACTIONS,
  CURRENT_SAVE_DATA_VERSION,
  ITEMS_FAILED_COUNT_FOR_LEVEL_FAIL,
  ITEMS_PER_ROUND,
} from '../../data/constants';

const initialState = {
  isLoaded: false,
  introComplete: false,
  savedFiles: [],
};

/**
 * Guards against corrupt saved game state written by the current save version.
 * A level whose round is already fully resolved (every clue complete or failed)
 * or whose strike count has reached the fail threshold should never have been
 * persisted as in-progress; such levels are reset to null and reported to RUM.
 * Entries are kept in place (index-preserving) so each still maps to its level index.
 * @param {Array<Object|null>} levels - saved level files
 * @returns {Array<Object|null>} same-length array with corrupt levels reset to null
 */
const getValidatedLevels = (levels = []) =>
  levels.map((level, index) => {
    if (!level) {
      return null;
    }

    if (
      level.strikeCount >= ITEMS_FAILED_COUNT_FOR_LEVEL_FAIL ||
      (level.clues || []).filter(({ isComplete, isFailed }) => isComplete || isFailed).length >= ITEMS_PER_ROUND
    ) {
      logRumError('Game state corruption: reset saved data', { gameId: GAME_ID, corruptedLevelIndex: index });
      return null;
    }

    return level;
  });

const saveDataReducer = (state = initialState, action) => {
  if (action.gameId !== GAME_ID) {
    return state;
  }

  switch (action.type) {
    case REDUX_ACTIONS.REPEAT_ONBOARDING:
      return {
        ...state,
        introComplete: false,
      };

    case REDUX_ACTIONS.COMPLETE_INTRO:
      return {
        ...state,
        introComplete: true,
      };
    case persistStateTypes.REQUESTERROR_GAMESTATE:
      return {
        ...state,
        isLoaded: true,
      };
    case persistStateTypes.REQUESTRESPONSE_GAMESTATE: {
      if (action.stateData[GAME_ID].GameData === undefined) {
        return {
          ...state,
          isLoaded: true,
        };
      }

      const { version = -1, savedFiles = [] } = action.stateData[GAME_ID].GameData;
      if (version < CURRENT_SAVE_DATA_VERSION) {
        return {
          ...state,
          isLoaded: true,
        };
      }

      return {
        ...state,
        isLoaded: true,
        savedFiles: getValidatedLevels(savedFiles),
      };
    }

    case REDUX_ACTIONS.START_LEVEL:
      return {
        ...state,
        introComplete: action.payload.introComplete,
      };

    case persistStateTypes.POSTRESPONSE_GAMESTATE: {
      let savedFiles = [...state.savedFiles];
      if (action.msg) {
        if (action.msg[GAME_ID].GameData.savedFiles !== undefined) {
          savedFiles = [...action.msg[GAME_ID].GameData.savedFiles];
        }
      }
      return {
        ...state,
        savedFiles,
      };
    }

    default:
      return state;
  }
};

export default saveDataReducer;
